/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState, memo } from 'react';
import { utcToZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';

import { RootState } from 'src/configs/store';
import formatOdds from '../../../utils/formatOdds';
import { Bet } from 'src/interfaces/BetslipState';
import BetHeader from './Components/BetHeader/BetHeader';
import './SportBet.scss';
import BetDetails from './Components/BetDetails/BetDetails';
import BetStakes from './Components/BetStakes/BetStakes';
import Icon from 'src/components/Icon/Icon';

interface SportBetProps {
  index?: number,
  bet: Bet,
  checkCombinable?: boolean;
  stake?: {
    stake?: number;
    maximumProfit?: number;
    profit: number;
  };
  rule?: {
    default: number;
    maxBetslip: number;
    maximum: number;
    minBetslip: number;
    minimum: number;
  };
  allowStakes?: boolean,
  showHeader: boolean,
  showTournament?: boolean,
  hideProfit: boolean,
  showBanker: boolean
}

function SportBet({ index,
  bet,
  checkCombinable,
  stake,
  rule,
  showHeader,
  allowStakes,
  showTournament=true,
  hideProfit,
  showBanker } : SportBetProps) {
  const [amount, setAmount] = useState<string>('0');
  const [validMessage, setValidMessage] = useState('');
  const [banker, setBanker] = useState<boolean>(false);

  const { t } = useTranslation();
  const selectedOddsType = useSelector(
    (state: RootState) => state.odds.selectedLanguage
  );
  const selectedSlip = useSelector((state: RootState) => state.betslip.slip);
  const divisor = useSelector((state: RootState) => state.betslip.divisor);
  const singleSlip = useSelector((state: RootState) => state.betslip.singleSlip);
  const systemSlip = useSelector((state: RootState) => state.betslip.systemSlip);
  const accaSlip = useSelector((state: RootState) => state.betslip.accaSlip);

  const dispatch = useDispatch();

  const stakeSelection = useSelector((state: RootState) => {
    return state.betslip.singleSlip.stakeSelection;
  }
  );

  const showCombinableWarning = false;
  const isSuspended = +bet.marketStatusNumber === 3 || +bet.outcomeStatusNumber === 3;

  const date = utcToZonedTime(bet.datetime, 'Europe/Lisbon');

  useEffect(() => {
    let amount = '0.00';
    if (selectedSlip === 0) {
      let value = singleSlip.stakes[index];
      if (stake && singleSlip.stakeSelection === 'totalStake') {
        value = stake.stake;
      } else if (singleSlip.stakeSelection === 'sameStake') {
        value = singleSlip.sameStake;
      }

      amount = (value / divisor).toString();
    }
    setAmount(amount);
    const stakeValue = singleSlip.stakes[index] / divisor;

    if (rule && rule.minimum > stakeValue) {
      setValidMessage(`${ t('betslip.stakeMinimumError') } ${ rule.minimum }`);
    } else if (rule && rule.maximum < stakeValue) {
      setValidMessage(`${ t('betslip.stakeMaximumError') } ${ rule.maximum }`);
    } else {
      setValidMessage('');
    }

    let _selectedSlip = singleSlip;
    if (selectedSlip === 1) _selectedSlip = accaSlip;
    else if (selectedSlip === 2) _selectedSlip = systemSlip;

    setBanker(!!_selectedSlip.bankers[index]);
  }, [bet, index, rule, selectedSlip, singleSlip, stake]);


  useEffect(() => {
    dispatch.betslip.updateBankerAtIndex({ index: index, isBanker: banker });
  }, [banker]);

  const handleUpdate = useCallback(() => {
    const stakeValue = singleSlip.stakes[index] / divisor;
    const value = rule.minimum > stakeValue ? rule.minimum : rule.maximum;
    dispatch.betslip.updateBetStake({ index, value });
  }, []);

  const onAmountChange = useCallback((name, value) => {
    dispatch.betslip.updateBetStake({ index, value: +value });
  }, []);

  const onBankerToggle = useCallback(() => {
    setBanker(!banker);
  }, [banker]);

  const suspendedName = +bet.marketStatusNumber === 3 ? bet.marketStatusName : bet.outcomeStatusName;

  return (
    <>
      <div className={
        classNames({
          'sport-bet-container': true,
          'sport-bet-container--combinable': showCombinableWarning,
          'sport-bet-container--suspended': isSuspended
        })
      }
      >
        {
          isSuspended && (
            <div className="sport-bet-container__suspended-bg" />
          )
        }
        {
          showHeader &&
        <BetHeader
          sport={ bet.sport }
          tournamentName={ bet.tournamentName }
          eventName={ bet.eventName }
          fixtureCode={ bet.fixtureCode }
          betCode={ bet.betCode }
          date={ date }
          venue={ bet.venue }
          showTournament={ showTournament }
        />
        }
        <BetDetails
          marketName={ bet.marketName }
          outcome={ bet.startNo ? `${ bet.startNo }. ${ bet.outcome }` : bet.outcome || bet.outcomeName }
          odds={ formatOdds(bet.odds, 'decimal') }
          index= { index }
          showBanker= { showBanker }
          selectedSlip = { selectedSlip }
          banker={ banker }
          onBankerToggle={ onBankerToggle }
          oddsChanged={ bet.oddsChanged }
        />
        {
          !hideProfit && <BetStakes
            validMessage ={ validMessage }
            allowStakes={ allowStakes }
            amount={ amount }
            divisor={ divisor }
            maximumProfit={ stake && stake.maximumProfit ? stake.maximumProfit : '0.00' }
            onAmountChange={ onAmountChange }
            marketStatusNumber={ +bet.marketStatusNumber }
            marketStatusName={ bet.marketStatusName }
            outcomeStatusNumber={ +bet.outcomeStatusNumber }
            outcomeStatusName={ bet.outcomeStatusName }
            combinationExclusions={ bet.combinationExclusions }
            index={ index }
            showCombinableWarning={ showCombinableWarning }
            handleUpdate={ handleUpdate }
          />
        }
        {
          hideProfit &&
          <div className="numberbet-stakes">
            <div className="bet-stakes__stake-container">
              {
                validMessage !== '' && stakeSelection === 'differentStake' &&(
                  <Icon icon="warningFilled" className="bet-stakes__warning-icon" />
                )
              }
            </div>
            {
              validMessage !== '' && stakeSelection === 'differentStake' &&(
                <div className="bet-stakes__minimum-warning"><span>
                  { validMessage }
                </span>
                <div onClick={ handleUpdate } className="bet-stakes__update">Update</div>
                </div>
              )
            }
            {
              showCombinableWarning && (
                <div className="bet-stakes__combinable">{ t('betslip.notCombinable') } { bet.combinationExclusions.toString() }</div>
              )
            }
            {
              isSuspended &&<div className="bet-stakes__suspended">
                <span>{ t('betslip.marketSuspended') }</span>
                <span>{ suspendedName }</span>
                <button
                  onClick={ () => dispatch.betslip.removeBetAtIndex(index) }
                  className="bet-stakes__btn-remove"
                >{ t('betslip.removeBet') }</button>
              </div>
            }
          </div>
        }
      </div>
    </>
  );
}

export default memo(SportBet);
